/* eslint-disable camelcase */
import { Button, SelectInput, TextInput, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { RuleCategory } from '../../../../../backend/shared/api-clients/_api/config';
import {
  CircuitType,
  CTMeter_SCHNEIDER_IEM3455,
  DeviceType,
  RuleActionType,
} from '../../../../shared/api-clients/_api';
import { useRoutePaths } from '../../../../shared/context/paths';
import { enumToArray, generateSelectOptions } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { requiredTextInputValidator } from '../../../../shared/hooks/Validators';
import { BackButton } from '../../components/buttons/back-button';
import { Spinner } from '../../components/spinner/spinner';
import { useBeeConfig } from '../../hooks/beeConfig';
import { routePaths } from '../../routes';

const SUPPORTED_DEVICE_TYPES = [
  DeviceType.EASTRON_SDM630MCT_4L,
  DeviceType.EASTRON_SDM630MCT_MV,
  DeviceType.SCHNEIDER_IEM3455,
];

type IClampForm = Omit<CTMeter_SCHNEIDER_IEM3455['params'], 'address' | 'channel' | 'id'>

export const ClampView: React.FunctionComponent = () => {
  const { push } = useRoutePaths();
  const { location } = useHistory<CTMeter_SCHNEIDER_IEM3455>();
  const { updateConfig, getDevicesByCategory, loading } = useBeeConfig();

  const newDevice = location.state;
  const [existingDevice] = getDevicesByCategory<CTMeter_SCHNEIDER_IEM3455>(RuleCategory.ELECTRICITY).filter(d =>
    SUPPORTED_DEVICE_TYPES.includes(d.deviceType)
  );

  const initialState: IClampForm = React.useMemo(() => {
    return existingDevice ? existingDevice.params : newDevice?.params;
  }, [existingDevice, newDevice]);

  const { DataForm, bind, dataBinder } = useForm<IClampForm>(initialState);

  const { validationErrors, setTouched, formIsValid } = useValidatedForm<IClampForm>(
    [
      {
        key: 'clamp_1_circuit_type',
        validators: [
          {
            validate: value => enumToArray(CircuitType).includes(value),
            message: 'Must be a valid circuit type',
          },
        ],
      },
      {
        key: 'clamp_1_circuit_label',
        validators: [requiredTextInputValidator],
      },
      {
        key: 'clamp_2_circuit_type',
        validators: [
          {
            validate: value => enumToArray(CircuitType).includes(value),
            message: 'Must be a valid circuit type',
          },
        ],
      },
      {
        key: 'clamp_2_circuit_label',
        validators: [requiredTextInputValidator],
      },
      {
        key: 'clamp_3_circuit_type',
        validators: [
          {
            validate: value => enumToArray(CircuitType).includes(value),
            message: 'Must be a valid circuit type',
          },
        ],
      },
      {
        key: 'clamp_3_circuit_label',
        validators: [requiredTextInputValidator],
      },
      {
        key: 'non_metered_circuits',
        validators: [requiredTextInputValidator],
      },
    ],
    dataBinder
  );

  const handleContinue = async () => {
    const device: CTMeter_SCHNEIDER_IEM3455 = {
      ...(existingDevice ? { ...existingDevice } : { ...newDevice }),
      params: {
        ...(existingDevice?.params ? { ...existingDevice?.params } : { ...newDevice?.params }),
        action_type: RuleActionType.MONITORING,
        clamp_1_circuit_type: dataBinder.getValue('clamp_1_circuit_type'),
        clamp_1_circuit_label: dataBinder.getValue('clamp_1_circuit_label'),
        clamp_2_circuit_type: dataBinder.getValue('clamp_2_circuit_type'),
        clamp_2_circuit_label: dataBinder.getValue('clamp_2_circuit_label'),
        clamp_3_circuit_type: dataBinder.getValue('clamp_3_circuit_type'),
        clamp_3_circuit_label: dataBinder.getValue('clamp_3_circuit_label'),
        non_metered_circuits: dataBinder.getValue('non_metered_circuits'),
      },
    };
    const inlineMeters = getDevicesByCategory(RuleCategory.ELECTRICITY).filter(
      d => !SUPPORTED_DEVICE_TYPES.includes(d.deviceType)
    );
    if (!existingDevice) {
      await updateConfig({ devicesToAdd: [device], devicesToRemove: inlineMeters });
    } else {
      await updateConfig({
        devicesToEdit: [{ currentDevice: existingDevice, updatedDevice: device }],
        devicesToRemove: inlineMeters,
      });
    }

    push(routePaths.heatAndFlowMeters.root);
  };

  const options = generateSelectOptions(CircuitType);

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />

      <h1 data-qa="ct-clamp-1-title">CT Clamp 1</h1>
      <div>
        <SelectInput
          {...bind.select('clamp_1_circuit_type')}
          data-qa="ct-clamp-1-circuit-type"
          label="Circuit Type"
          options={options}
          onBlur={setTouched}
        />
      </div>

      <div>
        <TextInput
          {...bind.text('clamp_1_circuit_label')}
          data-qa="ct-clamp-1-circuit-label"
          label="Enter description of circuits for this clamp"
          onBlur={setTouched}
        />
      </div>

      <h1 data-qa="ct-clamp-2-title">CT Clamp 2</h1>
      <div>
        <SelectInput
          {...bind.select('clamp_2_circuit_type')}
          data-qa="ct-clamp-2-circuit-type"
          label="Circuit Type"
          options={options}
          onBlur={setTouched}
        />
      </div>

      <div>
        <TextInput
          {...bind.text('clamp_2_circuit_label')}
          data-qa="ct-clamp-2-circuit-label"
          label="Enter description of circuits for this clamp"
          onBlur={setTouched}
        />
      </div>

      <h1 data-qa="ct-clamp-3-title">CT Clamp 3</h1>
      <div>
        <SelectInput
          {...bind.select('clamp_3_circuit_type')}
          data-qa="ct-clamp-3-circuit-type"
          label="Circuit Type"
          options={options}
          onBlur={setTouched}
        />
      </div>

      <div>
        <TextInput
          {...bind.text("clamp_3_circuit_label")}
          data-qa="ct-clamp-3-circuit-label"
          label="Enter description of circuits for this clamp"
          onBlur={setTouched}
        />
      </div>

      <h1 data-qa="non-metered-title">Non metered circuits</h1>

      <div>
        <TextInput
          {...bind.text('non_metered_circuits')}
          label="Enter description of circuits for this clamp"
          data-qa="ct-non-metered-circuits"
          onBlur={setTouched}
        />
      </div>

      <div className="row">
        <Button
          className="continue-button btn-full btn-large"
          disabled={!formIsValid}
          onClick={handleContinue}
          data-qa="continue-button"
        >
          Continue
        </Button>
      </div>
    </DataForm>
  );
};
